import useTranslation from 'next-translate/useTranslation';
import type {
  ContestSettings,
  PayoutSchedule,
  SlateLabelEnum,
} from '@betterpool/api-types/contests-service';
import { ResizingMode, ContestType } from '@betterpool/api-types/contests-service';
import { createStyles, useMantineTheme } from '@mantine/core';

import { Text } from '~/domains/design-system/Text';
import { UnstyledButtonLink } from '~/domains/design-system/Button';
import Modal from '~/domains/common/components/Modal/Modal/Modal';
import DrawerTitle from '~/domains/design-system/components/Drawer/DrawerTitle';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';

import { SectionNote } from '../components';

import PrizesSegmentsHeader from './PrizesSegmentsHeader';
import PrizesTable from './PrizeTable';
import { PrizesResizingMode, PrizesResizingInfo } from './PrizesResizingMode';
import PrizesEntryRevival from './PrizesEntryRevival';

type PrizesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  prizes: PayoutSchedule[];
  resizingMode?: ResizingMode;
  contestType?: ContestType;
  segments?: ContestSettings['segments'];
  slateLabel?: SlateLabelEnum;
  contestTags?: string[];
  buybackPrizePoolInDollars?: number | null;
  buybackSettings?: ContestSettings['buybacks'];
  contest: Contest;
};

const useStyles = createStyles((theme) => ({
  rule: {
    textDecoration: 'none',
  },
  buttonLink: {
    textDecoration: 'none',
    marginTop: theme.spacing.xs,
  },
}));

function PrizesModal({
  isOpen,
  onClose,
  prizes = [],
  resizingMode,
  segments,
  contestType,
  contestTags,
  slateLabel,
  buybackPrizePoolInDollars,
  buybackSettings,
  contest,
}: PrizesModalProps) {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { t } = useTranslation('contest');

  return (
    <Modal onClose={onClose} isOpened={isOpen} size="50%">
      <DrawerTitle isAddTopPaddingOnMobile> {t('detail.prizes.modalTitle')}</DrawerTitle>
      <SectionNote mt={20} mb={20}>
        <Text variant="body-medium">
          {(() => {
            if (contestType === ContestType.TEAM_SURVIVOR)
              return t('detail.prizes.survivorContest');
            if (contestTags.includes('multiplier')) return t('detail.prizes.multiplierContest');
            return t('detail.prizes.otherContests');
          })()}
        </Text>
      </SectionNote>
      <PrizesTable slateLabel={slateLabel} prizes={prizes} segments={segments} contest={contest} />
      <PrizesSegmentsHeader segments={segments} slateLabel={slateLabel} />
      <PrizesResizingMode resizingMode={resizingMode} contest={contest} />
      <PrizesResizingInfo
        resizingMode={resizingMode}
        segments={segments}
        slateLabel={slateLabel}
        contest={contest}
      />

      {resizingMode === ResizingMode.RESIZABLE ? (
        <UnstyledButtonLink
          href="https://legal.splashsports.com/faq-categories/contests"
          target="_blank"
          className={classes.rule}
        >
          <Text underline variant="body-medium" c={theme.colors.blue[9]}>
            {t('detail.prizes.action.link')}
          </Text>
        </UnstyledButtonLink>
      ) : null}
      {buybackSettings && (
        <PrizesEntryRevival
          buybackPrizePoolInDollars={buybackPrizePoolInDollars}
          buybackSettings={buybackSettings}
        />
      )}

      {buybackSettings && (
        <SectionNote>
          <UnstyledButtonLink
            href="https://legal.splashsports.com/legal/buyback-rules"
            target="_blank"
            className={classes.buttonLink}
          >
            <Text underline variant="body-medium" c={theme.colors.blue[9]}>
              {t('detail.prizes.entryRevival.revivalRules')}
            </Text>
          </UnstyledButtonLink>
        </SectionNote>
      )}
    </Modal>
  );
}

export default PrizesModal;

export type { PrizesModalProps };
